import type { FlagValues } from '@pokerly/shared-common';
import { computed, type ComputedRef, onMounted, ref, watch } from 'vue';
import { useApiClient } from '@/composables/use-api-client.ts';
import { useIntervalFn } from '@vueuse/core';
import { useAuthStore } from '@/stores/auth.ts';

const FEATURE_FLAGS_FETCH_INTERVAL = 60 * 1000;
interface FlagState {
    enabled: boolean;
    value?: string | boolean;
}

type FeatureFlagResponse = Record<FlagValues, FlagState>;

const _flagsState = ref<FeatureFlagResponse>();

export function useFeatureFlags(): void {
    const { execute, data } = useApiClient<FeatureFlagResponse>();
    const auth = useAuthStore();

    async function updateFlags() {
        await execute('/feature-flags');
        _flagsState.value = data.value ? data.value : ({} as FeatureFlagResponse);
    }

    onMounted(() => {
        useIntervalFn(updateFlags, FEATURE_FLAGS_FETCH_INTERVAL, { immediate: true, immediateCallback: true });
    });

    watch(
        () => auth.user,
        async () => {
            await updateFlags();
        },
    );
}

export function useFlags(flag: FlagValues): ComputedRef<FlagState>;
export function useFlags(flags: FlagValues[]): ComputedRef<FeatureFlagResponse>;
export function useFlags(flags: FlagValues | FlagValues[]): ComputedRef<FlagState | FeatureFlagResponse> {
    return computed(() => {
        if (!Array.isArray(flags)) return _flagsState.value?.[flags] ?? { enabled: false };
        const flagValues = {} as FeatureFlagResponse;
        if (flags.length > 0 && _flagsState.value) {
            flags.forEach((flag) => {
                flagValues[flag] = _flagsState.value?.[flag] ?? { enabled: false };
            });
        }
        return flagValues;
    });
}
